import { type Dayjs } from 'dayjs';

type TimelineListUnit = 'day' | 'month' | 'year';

export type TimelineListItem<T> =
	| { kind: 'marker'; key: string; primary: string; secondary: string; isAnchor: boolean; isNow: boolean }
	| { kind: 'gap'; key: string; label: string }
	| { kind: 'now'; key: string }
	| { kind: 'entry'; key: string; entry: T };

/**
 * Days within the current month and up to a month ahead of today, months within the previous, current
 * and next calendar year, years beyond. The forward day zone ends mid-month, so the month group just
 * past it holds only that month's remaining days; every other zone edge falls on a month/year boundary.
 */
function unitForDate(date: Dayjs, now: Dayjs): TimelineListUnit {
	if (date.isSame(now, 'month')) return 'day';
	if (date.isAfter(now) && date.isBefore(now.startOf('day').add(1, 'month'))) return 'day';
	const year = date.year();
	if (year >= now.year() - 1 && year <= now.year() + 1) return 'month';
	return 'year';
}

function plural(count: number, unit: string): string {
	return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

function relativeLabel(diff: number, unit: TimelineListUnit): string {
	if (unit === 'day') {
		if (diff === 0) return 'Today';
		if (diff === -1) return 'Yesterday';
		if (diff === 1) return 'Tomorrow';
	}
	if (unit === 'month') {
		if (diff === -1) return 'Last month';
		if (diff === 1) return 'Next month';
	}
	return diff < 0 ? `${plural(-diff, unit)} ago` : `in ${plural(diff, unit)}`;
}

function absoluteLabel(start: Dayjs, unit: TimelineListUnit, now: Dayjs): string {
	if (unit === 'day') return start.format(start.isSame(now, 'month') ? 'ddd D' : 'ddd D MMM');
	if (unit === 'month') return start.format(start.isSame(now, 'year') ? 'MMMM' : 'MMMM YYYY');
	return start.format('YYYY');
}

/** Length of an empty span, in the largest whole unit it covers. */
function durationLabel(from: Dayjs, to: Dayjs): string {
	const months = to.diff(from, 'month');
	if (months >= 12) return plural(Math.floor(months / 12), 'year');
	if (months >= 1) return plural(months, 'month');
	const days = to.diff(from, 'day');
	if (days >= 7) return plural(Math.floor(days / 7), 'week');
	return plural(days, 'day');
}

function markerFor<T>(start: Dayjs, unit: TimelineListUnit, now: Dayjs, isAnchor: boolean, isNow: boolean): TimelineListItem<T> {
	const diff = start.diff(now.startOf(unit), unit);
	return {
		kind: 'marker',
		key: `${unit}-${start.format('YYYY-MM-DD')}`,
		primary: relativeLabel(diff, unit),
		secondary: absoluteLabel(start, unit, now),
		isAnchor,
		isNow,
	};
}

/**
 * Lays dated entries out newest-first, grouped under a label per day, month or year depending on how
 * far the group is from today (see `unitForDate`). The first group not in the future is the anchor.
 * A gap item sits between two groups whenever at least one whole unit between them has no entries.
 * The current moment is marked once, right above the first entry dated at or before `currentMoment`, or
 * at the very end when every entry is in the future. Where that lands on today's label, the label itself
 * carries it (`isNow`); elsewhere a standalone `now` item does. Today always gets a label, empty if need be.
 */
export function buildTimelineListItems<T>(
	datedEntries: { entry: T; key: string; date: Dayjs }[],
	now: Dayjs,
	currentMoment: Dayjs,
): TimelineListItem<T>[] {
	const sorted = [...datedEntries].sort((a, b) => b.date.valueOf() - a.date.valueOf());
	const items: TimelineListItem<T>[] = [];
	const today = now.startOf('day');
	let previousStart: Dayjs | null = null;
	let hasAnchor = false;
	let hasNow = false;
	const openGroup = (start: Dayjs, unit: TimelineListUnit, isNow = false) => {
		const end = start.add(1, unit);
		if (previousStart && end.isBefore(previousStart)) {
			items.push({ kind: 'gap', key: `gap-${unit}-${start.format('YYYY-MM-DD')}`, label: durationLabel(end, previousStart) });
		}
		const isAnchor: boolean = !hasAnchor && !start.isAfter(now);
		hasAnchor = hasAnchor || isAnchor;
		items.push(markerFor(start, unit, now, isAnchor, isNow));
		previousStart = start;
	};
	/** At a group boundary: right below today's group if it exists, otherwise as an empty today group's label. */
	const placeNow = () => {
		if (previousStart?.isSame(today)) items.push({ kind: 'now', key: 'now' });
		else openGroup(today, 'day', true);
	};
	for (const { entry, key, date } of sorted) {
		const unit = unitForDate(date, now);
		const start = date.startOf(unit);
		const isNowHere: boolean = !hasNow && !date.isAfter(currentMoment);
		hasNow = hasNow || isNowHere;
		const opensGroup = !previousStart || !start.isSame(previousStart);
		const isTodayGroup = unit === 'day' && start.isSame(today);
		if (isNowHere && !opensGroup) items.push({ kind: 'now', key: 'now' });
		if (isNowHere && opensGroup && !isTodayGroup) placeNow();
		if (opensGroup) openGroup(start, unit, isNowHere && isTodayGroup);
		items.push({ kind: 'entry', key, entry });
	}
	if (!hasNow && sorted.length) placeNow();
	return items;
}
